import React, { useState } from 'react';
import { navigate } from 'gatsby';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddressContent, AddressValidationMessage, usePrefetchAddresses } from '@printdeal/address-frontend';
import {
  Alert, Button, Label, LinkAsButton, Panel, TextField,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { ErrorMessage } from '@hookform/error-message';
import useCreateChangeAscriptionMutation from '../queries/useCreateChangeAscriptionMutation';
import useChangeAscriptionValidationSchema, { ChangeAscriptionFormFields } from '../hooks/useChangeAscriptionValidationSchema';
import ChangeAscriptionAddressModal from '../ChangeAscriptionAddressModal';

interface Props {
  invoiceNumber: string;
  defaultValues: ChangeAscriptionFormFields;
  changeAscriptionMutation: ReturnType<typeof useCreateChangeAscriptionMutation>;
}

const ChangeAscriptionForm = ({
  invoiceNumber,
  defaultValues,
  changeAscriptionMutation,
}: Props) => {
  usePrefetchAddresses();

  const intl = useIntl();
  const schema = useChangeAscriptionValidationSchema();
  const {
    register, handleSubmit, getValues, setValue, formState,
  } = useForm<ChangeAscriptionFormFields>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues,
  });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const onSubmit = (data: ChangeAscriptionFormFields) => {
    changeAscriptionMutation.mutateAsync({
      invoiceNumber,
      poNumber: data.poNumber,
      reference: data.reference,
      vatNumber: data.vatId,
      billing: {
        firstName: data.billingAddress.firstName,
        lastName: data.billingAddress.lastName,
        streetName: data.billingAddress.streetName,
        houseNumber: data.billingAddress.streetNumber,
        additionalStreetInfo: data.billingAddress.additionalStreetInfo,
        postalCode: data.billingAddress.postalCode,
        city: data.billingAddress.city,
        countryCode: data.billingAddress.country,
        companyName: data.billingAddress.company,
        phone: data.billingAddress.phone,
        email: data.billingAddress.email as string,
      },
    }).then(() => {
      navigate('../..?changeAscriptionResult=success');
    });
  };

  return (
    <>
      <form id="change-ascription-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="pd-grid pd-grid-cols-1 pd-gap-4 md:pd-grid-cols-2">
          <div className="pd-flex pd-flex-col pd-gap-2">
            <Label>
              <FormattedMessage id="account.order.addresses.invoice.title" defaultMessage="Invoice address" />
            </Label>
            <Panel className="pd-flex pd-grow pd-flex-col pd-justify-between pd-gap-2">
              {getValues('billingAddress') ? (
                <AddressContent
                  showFirstAndLastName
                  showEmail
                  address={getValues('billingAddress')}
                />
              ) : (
                <Alert variant="error">
                  <FormattedMessage
                    id="account.invoices.ascription.noAddress"
                    defaultMessage="No default billing address found. Please update your billing address."
                  />
                </Alert>
              )}

              <Button
                variant="secondary"
                iconBefore="edit"
                className="pd-w-full pd-self-start md:pd-w-auto"
                onClick={() => setIsModalOpen(true)}
              >
                <FormattedMessage id="address.button.changeBillingAddress" defaultMessage="Change billing address" />
              </Button>
            </Panel>
          </div>
          <div className="pd-flex pd-flex-col pd-gap-2">
            <TextField
              {...register('vatId', {
                onChange(event) {
                  setValue('vatId', event.target.value.replace(/[ .]/g, ''), { shouldValidate: true });
                },
              })}
              id="vatId"
              label={intl.formatMessage({
                id: 'address.form.label.vatId',
                defaultMessage: 'VAT number',
              })}
              validation={formState.errors.vatId ? 'error' : undefined}
              validationMessage={(
                <ErrorMessage
                  errors={formState.errors}
                  name="vatId"
                  render={({ message }) => (
                    <AddressValidationMessage
                      fieldName="vatId"
                      errorMessage={message}
                    />
                  )}
                />
              )}
            />
            <TextField
              {...register('poNumber')}
              id="poNumber"
              label={intl.formatMessage({
                id: 'account.order.poNumber',
                defaultMessage: 'PO number',
              })}
              validation={formState.errors.poNumber ? 'error' : undefined}
              validationMessage={formState.errors.poNumber?.message}
            />
            <TextField
              {...register('reference')}
              id="reference"
              label={intl.formatMessage({
                id: 'account.order.orderReference',
                defaultMessage: 'Reference',
              })}
              validation={formState.errors.reference ? 'error' : undefined}
              validationMessage={formState.errors.reference?.message}
            />
          </div>
        </div>
      </form>

      <div className="pd-flex pd-flex-col-reverse pd-items-start pd-justify-between pd-gap-4 md:pd-flex-row md:pd-items-center">
        <LinkAsButton
          variant="secondary"
          iconBefore="chevron-left"
          className="pd-text-sm"
          onClick={() => navigate('../..')}
        >
          <FormattedMessage id="account.invoices.ascription.linkBackToInvoices" defaultMessage="Back to my invoices" />
        </LinkAsButton>

        <Button
          type="submit"
          form="change-ascription-form"
          className="pd-w-full md:pd-w-auto"
          isLoading={changeAscriptionMutation.isPending}
          disabled={changeAscriptionMutation.isPending || !formState.isDirty}
        >
          <FormattedMessage
            id="account.invoices.list.change.ascription.button"
            defaultMessage="Change ascription"
          />
        </Button>
      </div>

      <ChangeAscriptionAddressModal
        isOpen={isModalOpen}
        onSelect={(address) => {
          setValue('billingAddress', address, { shouldValidate: true, shouldDirty: true });
          setIsModalOpen(false);
        }}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default ChangeAscriptionForm;
